import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { cn } from '../lib/utils';
interface ThreeBackgroundProps {
  mousePosition: {
    x: number;
    y: number;
  };
  scrollProgress: number;
  className?: string;
}
export function ThreeBackground({
  mousePosition,
  scrollProgress,
  className
}: ThreeBackgroundProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  // Refs to hold mutable Three.js state without triggering re-renders
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const objectsRef = useRef<{
    mesh: THREE.Mesh;
    basePos: THREE.Vector3;
    speed: number;
    noiseOffset: number;
  }[]>([]);
  const targetMouseRef = useRef({
    x: 0,
    y: 0
  });
  const currentMouseRef = useRef({
    x: 0,
    y: 0
  });
  const targetScrollRef = useRef(0);
  const currentScrollRef = useRef(0);
  // Update refs when props change
  useEffect(() => {
    targetMouseRef.current = mousePosition;
  }, [mousePosition]);
  useEffect(() => {
    targetScrollRef.current = scrollProgress;
  }, [scrollProgress]);
  useEffect(() => {
    if (!containerRef.current) return;
    // 1. Setup Scene, Camera, Renderer
    const scene = new THREE.Scene();
    sceneRef.current = scene;
    // No fog to keep it clean, or very subtle fog
    scene.fog = new THREE.FogExp2(0x050505, 0.02);
    const camera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 0.1, 100);
    camera.position.z = 12;
    cameraRef.current = camera;
    const renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: true
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2)); // Cap pixel ratio for performance
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;
    // 2. Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.2);
    scene.add(ambientLight);
    const pointLight = new THREE.PointLight(0xc4917e, 2, 50); // Coral tinted light
    pointLight.position.set(5, 5, 5);
    scene.add(pointLight);
    const pointLight2 = new THREE.PointLight(0x0a0a0a, 5, 50); // Dark fill
    pointLight2.position.set(-5, -5, -5);
    scene.add(pointLight2);
    // 3. Materials
    const material = new THREE.MeshPhysicalMaterial({
      color: 0xc4917e,
      emissive: 0xc4917e,
      emissiveIntensity: 0.05,
      metalness: 0.1,
      roughness: 0.4,
      transparent: true,
      opacity: 0.12,
      transmission: 0.9,
      thickness: 1.5,
      side: THREE.DoubleSide,
      wireframe: false
    });
    // 4. Create Objects (Cymatic / Liquid forms)
    // We use Icosahedron with detail 16 for enough vertices to displace smoothly
    const geometry = new THREE.IcosahedronGeometry(1, 16);
    // Store original positions for vertex displacement
    const positionAttribute = geometry.attributes.position;
    const vertexData = [];
    for (let i = 0; i < positionAttribute.count; i++) {
      vertexData.push(new THREE.Vector3().fromBufferAttribute(positionAttribute, i));
    }
    geometry.userData = {
      originalPositions: vertexData
    };
    const objects = [{
      mesh: new THREE.Mesh(geometry.clone(), material.clone()),
      basePos: new THREE.Vector3(-4, 2, -2),
      scale: 3.5,
      speed: 0.2,
      noiseOffset: 0
    }, {
      mesh: new THREE.Mesh(geometry.clone(), material.clone()),
      basePos: new THREE.Vector3(5, -1, -4),
      scale: 2.8,
      speed: 0.15,
      noiseOffset: 100
    }, {
      mesh: new THREE.Mesh(geometry.clone(), material.clone()),
      basePos: new THREE.Vector3(0, -5, -6),
      scale: 4.0,
      speed: 0.1,
      noiseOffset: 200
    }];
    objects.forEach(obj => {
      obj.mesh.scale.setScalar(obj.scale);
      obj.mesh.position.copy(obj.basePos);
      scene.add(obj.mesh);
      objectsRef.current.push(obj);
    });
    // 5. Animation Loop
    const clock = new THREE.Clock();
    let animationFrameId: number;
    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);
      const time = clock.getElapsedTime();
      // Lerp mouse and scroll for smooth motion
      currentMouseRef.current.x += (targetMouseRef.current.x - currentMouseRef.current.x) * 0.05;
      currentMouseRef.current.y += (targetMouseRef.current.y - currentMouseRef.current.y) * 0.05;
      currentScrollRef.current += (targetScrollRef.current - currentScrollRef.current) * 0.05;
      objectsRef.current.forEach((obj, index) => {
        // Slow rotation
        obj.mesh.rotation.x = time * obj.speed + obj.noiseOffset;
        obj.mesh.rotation.y = time * (obj.speed * 0.8) + obj.noiseOffset;
        // Mouse repulsion & Scroll shift
        // Calculate distance from mouse to object (roughly mapped to screen space)
        const mouseInfluenceX = currentMouseRef.current.x * 2;
        const mouseInfluenceY = currentMouseRef.current.y * 2;
        // Push objects away from center/mouse slightly
        const targetX = obj.basePos.x + (obj.basePos.x > 0 ? 1 : -1) * Math.abs(mouseInfluenceX) * 0.5;
        const targetY = obj.basePos.y + (obj.basePos.y > 0 ? 1 : -1) * Math.abs(mouseInfluenceY) * 0.5;
        // Scroll moves objects up
        const scrollShift = currentScrollRef.current * 8; // Max shift upwards
        obj.mesh.position.x += (targetX - obj.mesh.position.x) * 0.02;
        obj.mesh.position.y += (targetY + scrollShift - obj.mesh.position.y) * 0.02;
        // Vertex displacement (Cymatic breathing effect)
        const geom = obj.mesh.geometry as THREE.BufferGeometry;
        const posAttr = geom.attributes.position;
        const origPositions = geom.userData.originalPositions as THREE.Vector3[];
        const v = new THREE.Vector3();
        for (let i = 0; i < posAttr.count; i++) {
          v.copy(origPositions[i]);
          // Simple pseudo-noise using sine waves based on position and time
          const noiseFreq = 1.5;
          const noiseAmp = 0.15;
          const noise = Math.sin(v.x * noiseFreq + time * 0.5 + obj.noiseOffset) * Math.cos(v.y * noiseFreq + time * 0.4) * Math.sin(v.z * noiseFreq + time * 0.3);
          // Displace along normal (which is same as position for a sphere centered at 0,0,0)
          v.addScaledVector(origPositions[i], noise * noiseAmp);
          posAttr.setXYZ(i, v.x, v.y, v.z);
        }
        posAttr.needsUpdate = true;
        geom.computeVertexNormals(); // Recompute normals for lighting
      });
      renderer.render(scene, camera);
    };
    animate();
    // 6. Resize Handler
    const handleResize = () => {
      if (!cameraRef.current || !rendererRef.current) return;
      cameraRef.current.aspect = window.innerWidth / window.innerHeight;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    // 7. Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameId);
      if (containerRef.current && rendererRef.current) {
        containerRef.current.removeChild(rendererRef.current.domElement);
      }
      // Dispose Three.js resources
      objectsRef.current.forEach(obj => {
        obj.mesh.geometry.dispose();
        (obj.mesh.material as THREE.Material).dispose();
      });
      rendererRef.current?.dispose();
    };
  }, []);
  return <div ref={containerRef} className={cn('three-canvas', className)} aria-hidden="true" data-id="element-62" />;
}